import React, {useState} from "react";
import {Button, Card, Text, styled} from "@reside/ui";

import {AdminRoles} from "../../../../atoms/admin-roles";
import {Column, ListTable} from "../../../../atoms/list-table";
import {IconButton} from "../../../../atoms/icon-button";
import {Switch} from "../../../../atoms/switch";
import {RoleNameEnum} from "../../../../utils/types/common";
import {FacilitySettingsSection} from "../facility-settings-form/FacilitySettingsSection";
import {useFacilitySettingsParams} from "../../hooks/useFacilitySettingsParams";
import {useWorkflowTriggersQuery} from "./hooks/useWorkflowTriggersQuery";
import {
  useCreateWorkflowTriggerMutation,
  useDeleteWorkflowTriggerMutation,
  useUpdateWorkflowTriggerMutation,
} from "./hooks/useWorkflowTriggerMutations";
import {WorkflowTriggerModal} from "./WorkflowTriggerModal";
import {WorkflowTriggerModel, WorkflowTriggerRequest} from "./types";
import {WorkflowOption} from "../../../../atoms/start-workflow/types";
import {TriggerSentence} from "./triggerSentence";

export const SECTION_WORKFLOW_TRIGGERS = "__workflow-triggers";

type ContentProps = Readonly<{
  templateName: string;
  workflowOptions: ReadonlyArray<WorkflowOption>;
}>;

type TriggerRow = {
  rowData: WorkflowTriggerModel;
};

const WorkflowTriggersSectionContent = ({
  templateName,
  workflowOptions,
}: ContentProps) => {
  const {facilityId} = useFacilitySettingsParams();
  const {data: facilityTriggers = []} = useWorkflowTriggersQuery({facilityId});
  // Triggers only fire for admissions on the template they were configured for, so hide
  // ones left over from a previous facility template rather than showing inert rows.
  const triggers = facilityTriggers.filter(
    trigger => trigger.templateName === templateName,
  );

  const {mutateAsync: createTrigger} = useCreateWorkflowTriggerMutation();
  const {mutate: updateTrigger, mutateAsync: updateTriggerAsync} =
    useUpdateWorkflowTriggerMutation();
  const {mutate: deleteTrigger} = useDeleteWorkflowTriggerMutation();

  const [editingTrigger, setEditingTrigger] = useState<
    WorkflowTriggerModel | undefined
  >();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openCreateModal = () => {
    setEditingTrigger(undefined);
    setIsModalOpen(true);
  };

  const openEditModal = (trigger: WorkflowTriggerModel) => {
    setEditingTrigger(trigger);
    setIsModalOpen(true);
  };

  // Returns the mutation promise so the modal stays open (keeping the entered values)
  // when the backend rejects the save, e.g. a duplicate initiating workflow + status.
  const handleSubmit = async (values: WorkflowTriggerRequest) => {
    if (editingTrigger) {
      await updateTriggerAsync({
        facilityId,
        triggerId: editingTrigger.id,
        trigger: values,
      });
    } else {
      await createTrigger({facilityId, trigger: values});
    }
  };

  const handleDelete = (trigger: WorkflowTriggerModel) => {
    if (
      window.confirm("Are you sure you want to delete this workflow trigger?")
    ) {
      deleteTrigger({facilityId, triggerId: trigger.id});
    }
  };

  const handleToggleActive = (
    trigger: WorkflowTriggerModel,
    active: boolean,
  ) => {
    updateTrigger({
      facilityId,
      triggerId: trigger.id,
      trigger: {active},
    });
  };

  return (
    <FacilitySettingsSection id={SECTION_WORKFLOW_TRIGGERS}>
      <StyledCard>
        <Text as="h2" fontSize="20px" fontWeight="bold">
          Workflow Triggers
        </Text>

        <ListTable
          basic
          widthPercent
          sortedData={triggers}
          getRowId={row => row.rowData.id}
          noRowsMessage="No workflow triggers configured for this facility."
        >
          <Column
            label="Trigger"
            width={75}
            cellRenderer={({rowData}: TriggerRow) => (
              <p>
                <TriggerSentence
                  trigger={rowData}
                  workflowOptions={workflowOptions}
                />
              </p>
            )}
          />
          <Column
            label="Status"
            width={5}
            stopRowClick
            cellRenderer={({rowData}: TriggerRow) => (
              <Switch
                checked={rowData.active}
                onChange={active => handleToggleActive(rowData, active)}
                data-test-id={`workflow-trigger-active-${rowData.id}`}
              />
            )}
          />
          <Column
            label=""
            width={8}
            stopRowClick
            cellRenderer={({rowData}: TriggerRow) => (
              <TriggerRowActions>
                <IconButton
                  color="gray100"
                  frameless
                  iconName="pencil"
                  onClick={() => openEditModal(rowData)}
                />
                <IconButton
                  color="gray100"
                  frameless
                  iconName="trash"
                  onClick={() => handleDelete(rowData)}
                />
              </TriggerRowActions>
            )}
          />
        </ListTable>

        <Button
          style={{alignSelf: "flex-start", marginTop: "16px"}}
          color="primary"
          outline
          onClick={openCreateModal}
        >
          Add Trigger
        </Button>

        <WorkflowTriggerModal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          trigger={editingTrigger}
          templateName={templateName}
          workflowOptions={workflowOptions}
          onSubmit={handleSubmit}
        />
      </StyledCard>
    </FacilitySettingsSection>
  );
};

const StyledCard = styled(Card)`
  padding: 24px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TriggerRowActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  width: 100%;
  button {
    max-width: 24px;
  }
`;

export type Props = ContentProps;

export const WorkflowTriggersSection = ({
  templateName,
  workflowOptions,
}: Props) => (
  <AdminRoles roles={[RoleNameEnum.RESIDE_ADMIN]}>
    <WorkflowTriggersSectionContent
      templateName={templateName}
      workflowOptions={workflowOptions}
    />
  </AdminRoles>
);
